import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, ArrowLeft, Calendar, MapPin, Users, CreditCard, Package } from 'lucide-react';

interface BookingConfirmationData {
  booking: {
    id: string;
    booking_number: string;
    full_name: string;
    email: string;
    phone: string;
    payment_method: string;
    payment_status: string;
    booking_status: string;
    created_at: string;
  };
  bookingData: {
    from_location: string;
    to_location: string;
    passengers: number;
    trip_type: string;
    service_type: string;
    total_price: number;
    arrival_date?: string;
    arrival_time?: string;
    airline?: string;
    flight_number?: string;
    return_date?: string;
    return_time?: string;
    return_airline?: string;
    return_flight_number?: string;
    amount_of_luggage?: string;
    remarks?: string;
    extra_items?: Array<{
      extra_item_id: string;
      name: string;
      price: number;
      quantity: number;
    }>;
    extras_cost?: number;
  };
}

const BookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { booking, bookingData } = location.state as BookingConfirmationData || {};

  if (!booking || !bookingData) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-16">
          <div className="container mx-auto px-4">
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-8 text-center">
                <h1 className="text-2xl font-bold text-foreground mb-4">Booking Not Found</h1>
                <p className="text-muted-foreground mb-6">
                  We couldn't find your booking information. Please try booking again.
                </p>
                <Button onClick={() => navigate('/transfers')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Transfers
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  const formatPaymentMethod = (method: string) => {
    switch (method) {
      case 'cash':
        return 'Cash Payment';
      case 'paypal':
        return 'PayPal';
      case 'stripe':
        return 'Credit/Debit Card';
      default:
        return method;
    }
  };

  const formatServiceType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatTripType = (type: string) => {
    return type === 'oneway' ? 'One Way' : 'Round Trip';
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <CheckCircle className="w-16 h-16 text-green-500" />
              </div>
              <h1 className="text-4xl font-bold text-foreground mb-4">
                Booking Confirmed!
              </h1>
              <p className="text-xl text-muted-foreground mb-2">
                Thank you for booking with us
              </p>
              <p className="text-lg text-muted-foreground">
                We will contact you very soon to confirm all the details
              </p>
            </div>

            {/* Booking Number */}
            <Card className="mb-8">
              <CardContent className="p-6 text-center">
                <h2 className="text-lg font-semibold text-muted-foreground mb-2">
                  Your Booking Number
                </h2>
                <div className="text-3xl font-bold text-primary font-mono">
                  {booking.booking_number}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Please keep this number for your records
                </p>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Customer Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    Customer Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="text-sm text-muted-foreground">Full Name:</span>
                    <p className="font-semibold">{booking.full_name}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Email:</span>
                    <p className="font-semibold">{booking.email}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Phone:</span>
                    <p className="font-semibold">{booking.phone}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Trip Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <MapPin className="w-5 h-5 mr-2" />
                    Trip Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="text-sm text-muted-foreground">Route:</span>
                    <p className="font-semibold">
                      {bookingData.from_location} → {bookingData.to_location}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Trip Type:</span>
                    <p className="font-semibold">{formatTripType(bookingData.trip_type)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Service:</span>
                    <p className="font-semibold">{formatServiceType(bookingData.service_type)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Passengers:</span>
                    <p className="font-semibold">{bookingData.passengers}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Flight Information */}
              {(bookingData.arrival_date || bookingData.airline) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Calendar className="w-5 h-5 mr-2" />
                      Flight Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {bookingData.arrival_date && (
                      <div>
                        <span className="text-sm text-muted-foreground">Arrival Date:</span>
                        <p className="font-semibold">{bookingData.arrival_date}</p>
                      </div>
                    )}
                    {bookingData.arrival_time && (
                      <div>
                        <span className="text-sm text-muted-foreground">Arrival Time:</span>
                        <p className="font-semibold">{bookingData.arrival_time}</p>
                      </div>
                    )}
                    {bookingData.airline && (
                      <div>
                        <span className="text-sm text-muted-foreground">Airline:</span>
                        <p className="font-semibold">{bookingData.airline}</p>
                      </div>
                    )}
                    {bookingData.flight_number && (
                      <div>
                        <span className="text-sm text-muted-foreground">Flight Number:</span>
                        <p className="font-semibold">{bookingData.flight_number}</p>
                      </div>
                    )}
                    
                    {bookingData.trip_type === 'roundtrip' && (bookingData.return_date || bookingData.return_airline) && (
                      <>
                        <Separator className="my-4" />
                        <h4 className="font-semibold text-sm text-muted-foreground">Return Flight</h4>
                        {bookingData.return_date && (
                          <div>
                            <span className="text-sm text-muted-foreground">Return Date:</span>
                            <p className="font-semibold">{bookingData.return_date}</p>
                          </div>
                        )}
                        {bookingData.return_time && (
                          <div>
                            <span className="text-sm text-muted-foreground">Return Time:</span>
                            <p className="font-semibold">{bookingData.return_time}</p>
                          </div>
                        )}
                        {bookingData.return_airline && (
                          <div>
                            <span className="text-sm text-muted-foreground">Return Airline:</span>
                            <p className="font-semibold">{bookingData.return_airline}</p>
                          </div>
                        )}
                        {bookingData.return_flight_number && (
                          <div>
                            <span className="text-sm text-muted-foreground">Return Flight Number:</span>
                            <p className="font-semibold">{bookingData.return_flight_number}</p>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Payment Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Payment Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="text-sm text-muted-foreground">Payment Method:</span>
                    <p className="font-semibold">{formatPaymentMethod(booking.payment_method)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Total Amount:</span>
                    <p className="text-2xl font-bold text-primary">${bookingData.total_price.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Payment Status:</span>
                    <p className="font-semibold capitalize">{booking.payment_status}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Extra Items */}
              {bookingData.extra_items && bookingData.extra_items.length > 0 && (
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Package className="w-5 h-5 mr-2" />
                      Extra Items
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {bookingData.extra_items.map((item, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold">{item.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Quantity: {item.quantity}
                          </p>
                        </div>
                        <p className="font-semibold">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    ))}
                    <Separator className="my-2" />
                    <div className="flex justify-between items-center font-bold">
                      <span>Extras Total:</span>
                      <span>${(bookingData.extras_cost || 0).toFixed(2)}</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Additional Information */}
            {(bookingData.amount_of_luggage || bookingData.remarks) && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Additional Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {bookingData.amount_of_luggage && (
                    <div>
                      <span className="text-sm text-muted-foreground">Amount of Luggage:</span>
                      <p className="font-semibold">{bookingData.amount_of_luggage}</p>
                    </div>
                  )}
                  {bookingData.remarks && (
                    <div>
                      <span className="text-sm text-muted-foreground">Remarks:</span>
                      <p className="font-semibold">{bookingData.remarks}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="text-center mt-8 space-x-4">
              <Button onClick={() => navigate('/')} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
              <Button onClick={() => navigate('/transfers')}>
                Book Another Transfer
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BookingConfirmation;